import React from 'react';
import { AvatarPickerDialog, type Avatar } from '@atlaskit/media-avatar-picker';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { PACKAGE_NAME, TEAM } from '../../../../../common/constants';
import { commonMessages } from '../../../../../common/messages';
import type {
	CropProperties,
	PostAvatarResponse,
	PriorityAvatar,
} from '../../../../../common/types';
import { useAvatarPicker } from '../../../../../controllers/avatar-picker';
import { fetchAndMapPriorityAvatars } from '../../../../../services/get-avatars';
import { postAvatar } from '../../../../../services/post-avatar';
import { AvatarErrorModal } from './avatar-error-modal';
import { messages } from './messages';

type ModalAvatarPickerProps = {
	setSelectedAvatar: (selectedAvatar: PriorityAvatar | undefined) => void;
	onClose: () => void;
};

export const findPickedAvatar = (
	avatar: Avatar | PostAvatarResponse,
	avatars: PriorityAvatar[],
) => {
	let pickedAvatar: PriorityAvatar | undefined;
	let errorMessage: string | undefined;
	if ('id' in avatar) {
		pickedAvatar = avatars.find((avatarListItem) => avatarListItem.id === avatar.id);
		errorMessage = 'Uploaded avatar not found in avatar list';
	} else {
		pickedAvatar = avatars.find((avatarListItem) => avatarListItem.dataURI === avatar.dataURI);
		errorMessage = 'Selected avatar not found in avatar list';
	}

	if (!pickedAvatar) throw new Error(errorMessage);

	return pickedAvatar;
};

export const uploadPriorityAvatar = async (
	file: File,
	crop: CropProperties,
	accountId: string | null,
	setSelectedAvatar: (selectedAvatar: PriorityAvatar | undefined) => void,
	onClose: () => void,
	/**
	 * This callback is used to set an error message, we cannot pass setErrorMessage into
	 * this local function as formatMessage depends on a custom hook.
	 */
	onUploadAvatarError: () => void,
) => {
	try {
		if (!accountId) throw new Error('No account ID');

		const postAvatarResponse = await postAvatar(
			{ type: 'priority', entityId: accountId },
			{ file, crop },
		);

		const priorityAvatars = await fetchAndMapPriorityAvatars(accountId);

		setSelectedAvatar(findPickedAvatar(postAvatarResponse, priorityAvatars));
		onClose();
	} catch (error) {
		fireErrorAnalytics({
			error: new Error(`uploading custom avatar failed with error: ${error}`),
			meta: {
				id: 'onPriorityAvatarUploaded',
				packageName: PACKAGE_NAME,
				teamName: TEAM,
			},
		});
		onUploadAvatarError();
	}
};

export const ModalAvatarPicker = ({ setSelectedAvatar, onClose }: ModalAvatarPickerProps) => {
	/**
	 * I would have loved to move these states into the priority modal index file and keep
	 * this component dumb, but the atlaskist form component relies on setFormValue in order
	 * to update the form. Hence this state needs to be at minimum within the form component
	 * to access the formProps and setFieldValue.
	 *
	 * This was the neatest solution I came up with it isolates the avatar picker state
	 * and logic from the priority modal component.
	 */
	const { formatMessage } = useIntl();
	const accountId: string | null = useAccountId();
	const { avatars, errorMessage, setErrorMessage } = useAvatarPicker();

	const onAvatarPicked = (avatar: Avatar) => {
		try {
			const pickedAvatar = findPickedAvatar(avatar, avatars);
			setSelectedAvatar(pickedAvatar);
			onClose();
		} catch (error) {
			fireErrorAnalytics({
				error: new Error(`selecting priority avatar failed with error: ${error}`),
				meta: {
					id: 'onPriorityAvatarPicked',
					packageName: PACKAGE_NAME,
					teamName: TEAM,
				},
			});
			setErrorMessage(formatMessage(messages.avatarPickerGenericError));
		}
	};

	const onImagePicked = async (file: File, crop: CropProperties) => {
		/**
		 * Extracted this function, because RTL could not update the internal state of the
		 * avatar picker dialog such that this callback could be triggered. As such, we extracted
		 * the function so that we can test our callback. Another option would be to move this function
		 * into the useAvatarPicker controller, but our team whats to keep this logic with the component.
		 */
		uploadPriorityAvatar(file, crop, accountId, setSelectedAvatar, onClose, () =>
			setErrorMessage(formatMessage(messages.avatarPickerGenericError)),
		);
	};

	return errorMessage ? (
		<AvatarErrorModal errorMessage={errorMessage} onClose={onClose} />
	) : (
		<AvatarPickerDialog
			avatars={avatars}
			onAvatarPicked={onAvatarPicked}
			onImagePicked={onImagePicked}
			onCancel={onClose}
			primaryButtonText={formatMessage(messages.avatarPickerModalPrimaryButton)}
			title={formatMessage(commonMessages.avatarPickerModalHeader)}
		/>
	);
};
